import { useEffect, useState } from 'react';
import { ArrowDown, ArrowDownToLine, ArrowUp, ArrowUpToLine, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  SectionCard,
  Skeleton,
  toast,
} from '@/components/ui';
import { ApiClientError } from '@/lib/http-core';
import { friendlyMessage } from '@/lib/errors';
import { useHasRole } from '@/providers/AuthProvider';
import {
  COURIER_WRITE_ROLES,
  isValidCourierCode,
  isValidTrackingUrl,
  useCourierMutations,
  useShippingCouriersQuery,
  type CourierInput,
  type ShippingCourier,
} from '../hooks/useShippingCouriers';

type Editing = { mode: 'create' } | { mode: 'edit'; courier: ShippingCourier } | null;

const EMPTY: CourierInput = { nameKo: '', nameEn: '', trackingUrlTemplate: '', active: true };

const CourierFormDialog = ({ editing, onClose }: { editing: Editing; onClose: () => void }) => {
  const { create, update } = useCourierMutations();
  const isCreate = editing?.mode === 'create';
  const base = editing?.mode === 'edit' ? editing.courier : null;
  const [code, setCode] = useState('');
  const [input, setInput] = useState<CourierInput>(
    base
      ? { nameKo: base.nameKo, nameEn: base.nameEn ?? '', trackingUrlTemplate: base.trackingUrlTemplate ?? '', active: base.active }
      : EMPTY,
  );
  const codeOk = !isCreate || isValidCourierCode(code);
  const urlOk = isValidTrackingUrl(input.trackingUrlTemplate);
  const canSave = codeOk && urlOk && input.nameKo.trim() !== '';
  const pending = create.isPending || update.isPending;

  const onSave = () => {
    const done = {
      onSuccess: () => {
        toast.success('저장되었습니다.');
        onClose();
      },
      onError: (err: unknown) =>
        toast.error(
          err instanceof ApiClientError && err.status === 409
            ? '같은 코드의 업체가 이미 있습니다.'
            : friendlyMessage(err, '저장하지 못했습니다.'),
        ),
    };
    if (isCreate) create.mutate({ courierCode: code, input }, done);
    else if (base) update.mutate({ courierCode: base.courierCode, input }, done);
  };

  return (
    <Dialog open={editing !== null} onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <DialogContent className="max-w-lg" data-testid="courier-form-dialog">
        <DialogHeader>
          <DialogTitle>{isCreate ? '배송 업체 추가' : `배송 업체 수정 — ${base?.courierCode ?? ''}`}</DialogTitle>
          <DialogDescription>송장 입력·주문 상세·쇼핑몰 배송조회에 쓰입니다. 코드는 만든 뒤 바꿀 수 없습니다.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 text-[13px]">
          {isCreate ? (
            <label className="flex flex-col gap-1">
              업체 코드
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="예: ABC_EXPRESS (영문 대문자·숫자·_ 2~20자)"
                maxLength={20}
                aria-invalid={!codeOk && code !== ''}
                data-testid="courier-code"
              />
            </label>
          ) : null}
          <label className="flex flex-col gap-1">
            업체명
            <Input value={input.nameKo} maxLength={100} onChange={(e) => setInput({ ...input, nameKo: e.target.value })} data-testid="courier-name-ko" />
          </label>
          <label className="flex flex-col gap-1">
            영문명 (선택)
            <Input value={input.nameEn} maxLength={100} onChange={(e) => setInput({ ...input, nameEn: e.target.value })} />
          </label>
          <label className="flex flex-col gap-1">
            배송조회 URL (선택)
            <Input
              value={input.trackingUrlTemplate}
              onChange={(e) => setInput({ ...input, trackingUrlTemplate: e.target.value })}
              placeholder="https://…?invoice={trackingNo}"
              aria-invalid={!urlOk}
              data-testid="courier-url"
            />
            <span className="text-[12px]" style={{ color: urlOk ? 'var(--content-muted)' : 'var(--signal-danger)' }}>
              송장번호 자리에 {'{trackingNo}'} 를 넣어 주세요(고도몰 __INVOICENO__ 도 됩니다). 비우면 배송조회 링크가 없습니다.
            </span>
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2">
            <Checkbox checked={input.active} onCheckedChange={(v) => setInput({ ...input, active: v === true })} data-testid="courier-active" />
            사용 (송장 입력 택배사 목록에 나옴)
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            취소
          </Button>
          <Button variant="cta" size="sm" disabled={!canSave || pending} onClick={onSave} data-testid="courier-save">
            저장
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


/** 순서 옮기기 — 고른 행을 맨 위·위·아래·맨 아래로. */
export type MoveKind = 'top' | 'up' | 'down' | 'bottom';
export const moveCourier = <T,>(list: ReadonlyArray<T>, index: number, kind: MoveKind): T[] => {
  const next = [...list];
  if (index < 0 || index >= next.length) return next;
  const [item] = next.splice(index, 1);
  const to =
    kind === 'top' ? 0 : kind === 'bottom' ? next.length : kind === 'up' ? Math.max(0, index - 1) : Math.min(next.length, index + 1);
  next.splice(to, 0, item as T);
  return next;
};

const MOVE_BUTTONS: ReadonlyArray<{ kind: MoveKind; label: string; Icon: typeof ArrowUp }> = [
  { kind: 'top', label: '맨 위로', Icon: ArrowUpToLine },
  { kind: 'up', label: '위로', Icon: ArrowUp },
  { kind: 'down', label: '아래로', Icon: ArrowDown },
  { kind: 'bottom', label: '맨 아래로', Icon: ArrowDownToLine },
];

const COLS = 'grid-cols-[40px_52px_minmax(110px,1fr)_70px_minmax(150px,2fr)_minmax(180px,2.4fr)_70px_92px]';

/**
 * QA-510 [기본설정 > 배송 업체 관리] — 고도몰 [배송 정책 > 배송 업체 관리]와 같은 구성.
 * 행을 골라 맨 위·위·아래·맨 아래로 옮긴 뒤 [순서 저장], 행 [수정](이름·URL·사용 여부), [추가], [삭제](직접 추가 업체만).
 * "사용"으로 설정된 업체 중 가장 위 업체가 송장 입력 기본값이다. 변경은 SUPER_ADMIN·OPS_ADMIN, 그 외 읽기 전용.
 */
export const CourierManagementSection = () => {
  const query = useShippingCouriersQuery();
  const { reorder, remove } = useCourierMutations();
  const canWrite = useHasRole(COURIER_WRITE_ROLES);
  const [editing, setEditing] = useState<Editing>(null);
  const [order, setOrder] = useState<ShippingCourier[]>([]);
  const [selected, setSelected] = useState<string | null>(null);

  // 서버 목록이 바뀌면(저장·추가·수정 후) 화면 순서를 다시 맞춘다.
  useEffect(() => {
    setOrder(query.data ?? []);
  }, [query.data]);

  const saved = (query.data ?? []).map((c) => c.courierCode).join(',');
  const dirty = order.map((c) => c.courierCode).join(',') !== saved;
  const selectedIndex = order.findIndex((c) => c.courierCode === selected);

  const move = (kind: MoveKind) => setOrder((list) => moveCourier(list, selectedIndex, kind));

  const onSaveOrder = () => {
    reorder.mutate(
      order.map((c) => c.courierCode),
      {
        onSuccess: () => toast.success('순서를 저장했습니다.'),
        onError: (err) => toast.error(friendlyMessage(err, '순서를 저장하지 못했습니다.')),
      },
    );
  };

  const onDelete = (c: ShippingCourier) => {
    if (!window.confirm(`'${c.nameKo}' 업체를 삭제할까요?`)) return;
    remove.mutate(c.courierCode, {
      onSuccess: () => toast.success('삭제했습니다.'),
      onError: (err) =>
        toast.error(
          err instanceof ApiClientError && err.status === 409
            ? '주문에 쓰인 업체라 삭제할 수 없습니다. 수정에서 "미사용"으로 바꿔 주세요.'
            : friendlyMessage(err, '삭제하지 못했습니다.'),
        ),
    });
  };

  return (
    <SectionCard
      title="배송 업체 관리"
      description={'"사용"으로 설정된 업체 중 가장 위 업체가 송장 입력 기본값입니다.'}
      aside={
        canWrite ? (
          <Button size="sm" onClick={() => setEditing({ mode: 'create' })} data-testid="courier-add">
            <Plus size={14} aria-hidden="true" />
            추가
          </Button>
        ) : undefined
      }
      data-testid="courier-management"
    >
      {query.isPending ? (
        <Skeleton className="h-[320px] w-full" />
      ) : query.isError ? (
        <div role="alert" className="flex flex-col items-center gap-2 py-8 text-sm" style={{ color: 'var(--status-danger-fg)' }}>
          <p>배송 업체를 불러오지 못했습니다.</p>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            다시 시도
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {canWrite ? (
            <div className="flex flex-wrap items-center gap-2" data-testid="courier-move-bar">
              {MOVE_BUTTONS.map(({ kind, label, Icon }) => (
                <Button
                  key={kind}
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={selectedIndex < 0}
                  onClick={() => move(kind)}
                  aria-label={label}
                  title={label}
                  data-testid={`courier-move-${kind}`}
                >
                  <Icon size={14} aria-hidden="true" />
                </Button>
              ))}
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                {selectedIndex < 0 ? '행을 골라 순서를 옮긴 뒤 저장하세요.' : `${selectedIndex + 1}번 행을 옮깁니다.`}
              </span>
              <Button
                type="button"
                variant="cta"
                size="sm"
                className="ml-auto"
                disabled={!dirty || reorder.isPending}
                onClick={onSaveOrder}
                data-testid="courier-save-order"
              >
                순서 저장
              </Button>
            </div>
          ) : null}
          <div role="table" aria-label="배송 업체" className="flex flex-col text-[13px]">
            <div
              role="row"
              className={`grid ${COLS} items-center gap-2 px-2 py-2 text-[12px] font-medium`}
              style={{ color: 'var(--content-muted)', borderBottom: '1px solid var(--divider-strong)' }}
            >
              <span role="columnheader" />
              <span role="columnheader">번호</span>
              <span role="columnheader">배송업체코드</span>
              <span role="columnheader">고도몰 번호</span>
              <span role="columnheader">배송업체명</span>
              <span role="columnheader">배송조회 URL</span>
              <span role="columnheader">사용 여부</span>
              <span role="columnheader" />
            </div>
            {order.map((c, index) => {
              const isSel = c.courierCode === selected;
              return (
                <div
                  key={c.courierCode}
                  role="row"
                  aria-selected={isSel}
                  className={`grid ${COLS} items-center gap-2 px-2 py-1.5`}
                  style={{
                    borderBottom: '1px solid var(--divider-default, var(--divider-strong))',
                    background: isSel ? 'var(--surface-1)' : undefined,
                    opacity: c.active ? 1 : 0.6,
                  }}
                  data-testid={`courier-row-${c.courierCode}`}
                >
                  <span role="cell">
                    {canWrite ? (
                      <input
                        type="radio"
                        name="courier-select"
                        checked={isSel}
                        onChange={() => setSelected(c.courierCode)}
                        aria-label={`${c.nameKo} 고르기`}
                        data-testid={`courier-select-${c.courierCode}`}
                      />
                    ) : null}
                  </span>
                  <span role="cell" className="tabular-nums text-[12px]" style={{ color: 'var(--content-muted)' }}>
                    {index + 1}
                  </span>
                  <span role="cell" className="font-mono text-[12px]">
                    {c.courierCode}
                  </span>
                  <span role="cell" className="tabular-nums text-[12px]" style={{ color: 'var(--content-muted)' }}>
                    {c.godoSno ?? '—'}
                  </span>
                  <span role="cell" className="truncate" title={c.nameEn ?? undefined}>
                    {c.nameKo}
                    {c.custom ? (
                      <span className="ml-1 text-[11px]" style={{ color: 'var(--content-muted)' }}>
                        (직접 추가)
                      </span>
                    ) : null}
                  </span>
                  <span role="cell" className="truncate text-[12px]" style={{ color: 'var(--content-muted)' }} title={c.trackingUrlTemplate ?? undefined}>
                    {c.trackingUrlTemplate ?? '배송조회 없음'}
                  </span>
                  <span role="cell" className="text-[12px] font-medium" style={{ color: c.active ? 'var(--signal-success, #16A34A)' : 'var(--content-soft)' }}>
                    {c.active ? '사용' : '미사용'}
                  </span>
                  <span role="cell" className="flex justify-end gap-1">
                    {canWrite ? (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditing({ mode: 'edit', courier: c })}
                          aria-label={`${c.nameKo} 수정`}
                          data-testid={`courier-edit-${c.courierCode}`}
                        >
                          <Pencil size={14} aria-hidden="true" />
                        </Button>
                        {c.custom ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDelete(c)}
                            aria-label={`${c.nameKo} 삭제`}
                            data-testid={`courier-delete-${c.courierCode}`}
                          >
                            <Trash2 size={14} aria-hidden="true" />
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {editing ? (
        <CourierFormDialog key={editing.mode === 'edit' ? editing.courier.courierCode : 'new'} editing={editing} onClose={() => setEditing(null)} />
      ) : null}
    </SectionCard>
  );
};
